export class Ajax {

  /**
   * Make a post request to wordpress
   * @param action
   * @param formData
   * @param callback (err, data) => {}
   */
  static  postRequest(action, formData, callback) {
    if (!formData) {
      formData = new FormData();
    }
    formData.append("action", action);

    const headers = new Headers();
    
    // Add WordPress nonce header if available
    if (window.wp_ajax && window.wp_ajax.NONCE) {
      headers.append('X-WP-Nonce', window.wp_ajax.NONCE);
    }

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: headers,
    };

    fetch(window.wp_ajax.AJAX_URL, requestOptions)
      .then(response => {
        if(response.status === 413) {
          throw new Error(`The files you uploaded were too large. Make sure the total size is less than ${this.MAX_UPLOAD_SIZE_MB} MB.`);
        }

        if(response.status !== 200) {
          throw new Error(`An unexpected error occurred. Received a status code of ${response.status} from the server.`);
        }

        return response.json();
      })
      .then(result => {
        if(callback) {
          callback(null, result);
        }
      })
      .catch(error => {
        if(callback) {
          callback(error);
        }
      });
  }

  /**
   * Get search results from wordpress. Returns
   * product card HTML.
   *
   * @param callback
   * @param searchTerm
   * @param page
   * @param filters Array of objects with name and value. If null, the URL filter params will be used.
   * @param postType The post type to search for. Default is 'product'.
   */
  static getSearchResults(callback, searchTerm, page, filters, postType, customOnly = false, customizerMode = false, categoryHide = null, collectionHide = null, specHide = null, themeHide = null, audienceHide = null, decorationTypeHide = null, productTypeHide = null) {
    const formData = new FormData();
    if(searchTerm) {
      formData.append('s', searchTerm);
    }
    if(page) {
      formData.append('paged', page);
    }
    if(postType) {
      formData.append('post_type', postType === 'search' ? 'product' : postType);
    }
    if(customOnly) {
      formData.append('custom_only', 'true');
    }
    if(customizerMode) {
      formData.append('customizer_mode', 'true');
    }
    if(categoryHide) {
      formData.append('hide_category', categoryHide);
    }
    if(collectionHide) {
      formData.append('hide_collection', collectionHide);
    }
    if(specHide) {
      formData.append('hide_spec', specHide);
    }
    if(themeHide) {
      formData.append('hide_theme', themeHide);
    }
    if(audienceHide) {
      formData.append('hide_audience', audienceHide);
    }
    if(decorationTypeHide) {
      formData.append('hide_decoration_type', decorationTypeHide);
    }
    if(productTypeHide) {
      formData.append('hide_product_type', productTypeHide);
    }

    if(!filters) {
      filters = [];
    }

    const url = new URL(window.location.href);
    const params = url.searchParams;
    params.forEach((value, name) => {
      filters.push({name, value});
    });

    filters.forEach(filter => {
      if(!filter.value || filter.value.length === 0) return;

      formData.append(filter.name, filter.value);
    });

    this.postRequest('load_search_results', formData, callback);
  }

  static getRegionList(country, callback, useCode = false) {
    const formData = new FormData();
    formData.append('country', country);
    formData.append('use_code', useCode ? 'true' : 'false');

    this.postRequest('get_region_list', formData, callback);
  }

  static reloadFilterHtml(callback, searchTerm, filters, postType) {
    const formData = new FormData();
    if(searchTerm) {
      formData.append('s', searchTerm);
    }
    if(postType) {
      formData.append('post_type', postType);
    }

    if(!filters) {
      filters = [];
    }

    filters.forEach(filter => {
      if(!filter.value || filter.value.length === 0) return;

      formData.append(filter.name, filter.value);
    });

    this.postRequest('reload_filter_html', formData, callback);
  }

  static addCreditCard(form, callback) {
    const formData = new FormData(form);
    this.postRequest('add_credit_card', formData, callback);
  }

  static incrementDesignConceptViews(designConceptId, callback) {
    const formData = new FormData();
    formData.append('post_id', designConceptId);

    this.postRequest('increment_design_concept_view', formData, callback);
  }

  static getToastMessageMarkup(message, icon, action, callback) {
    const formData = new FormData();
    formData.append('message', message);
    if(icon) {
      formData.append('icon', icon);
    }
    if(action) {
      formData.append('action_message', action);
    }

    this.postRequest('get_toast_message_markup', formData, callback);
  }

  static getBlanksFormTableMarkup(model, colorway, product, colorwayType, callback) {
    const formData = new FormData();
    formData.append('model', model);
    formData.append('colorway', colorway);
    formData.append('product', product);
    formData.append('colorway_type', colorwayType);

    this.postRequest('get_blanks_form_table_markup', formData, callback);
  }

  static getModelSearchResults(model, excludeNonCustomizable, callback) {
    const formData = new FormData();
    formData.append('model', model);
    formData.append('exclude_non_customizable', excludeNonCustomizable ? 'true' : 'false');

    this.postRequest('get_model_search_results', formData, callback);
  }

  static getProductSearchResults(model, description = '', isInStock = false, isDecoration = false, callback) {
    const formData = new FormData();
    formData.append('model', model);
    formData.append('description', description);
    formData.append('is_in_stock', isInStock);
    formData.append('is_decoration', isDecoration);

    this.postRequest('get_product_search_results', formData, callback);
  }

  static getPositionFormMarkup(positionNumber, callback) {
    const formData = new FormData();
    formData.append('positionNumber', positionNumber);
    this.postRequest('get_position_form_markup', formData, callback);
  }

  static getStyleOptions(changingSelect, selectValue, callback) {
    const formData = new FormData();
    formData.append('changingSelect', changingSelect);
    formData.append('selectValue', selectValue);
    this.postRequest('get_style_options', formData, callback);
  }

  static getInventoryLookupReport(model, isInStock = false, callback) {
    const formData = new FormData();
    formData.append('model', model);
    formData.append('is_in_stock', isInStock);

    this.postRequest('get_inventory_lookup_report', formData, callback);
  }

  static validateAddress(address, callback) {
    const formData = new FormData();

    for(const key in address) {
      formData.append(key, address[key]);
    }

    this.postRequest('validate_address', formData, callback);
  }

  static addAddress(mode = 'user', address, saveToProfile, callback) {
    const formData = new FormData();

    formData.append('mode', mode);
    for(const key in address) {
      formData.append(key, address[key]);
    }
    formData.append('save_to_address_book', saveToProfile);

    this.postRequest('upsert_address', formData, callback);
  }

  static updateAddress(mode = 'user', addressId, address, callback) {
    const formData = new FormData();

    formData.append('id', addressId);
    formData.append('mode', mode);
    for(const key in address) {
      formData.append(key, address[key]);
    }

    this.postRequest('upsert_address', formData, callback);
  }

  static setAddressOnCart(addressId, callback) {
    const formData = new FormData();
    formData.append('id', addressId);

    this.postRequest('set_shipping_address', formData, callback);
  }

  static setShippingMethodOnCart(methodCode, carrierCode, collectNumber, title, callback) {
    const formData = new FormData();
    formData.append('method_code', methodCode);
    formData.append('carrier_code', carrierCode);
    formData.append('collect_number', collectNumber ? collectNumber : '');
    formData.append('title', title ? title : '');

    this.postRequest('set_shipping_method', formData, callback);
  }

  static uploadFileToAWS(file, callback) {
    const formData = new FormData();
    formData.append('file', file);

    this.postRequest('upload_file_to_aws', formData, callback);
  }

  static getReorderParts(orderId, callback) {
    const formData = new FormData();
    formData.append('order_id', orderId);

    this.postRequest('get_reorder_parts', formData, callback);
  }

  static loadModelInfo(model, callback) {
    const formData = new FormData();
    formData.append('model', model);

    this.postRequest('load_model_info', formData, callback);
  }

  static getShippingRates(address, caps, strawHats, brimmedHats, warehouses, callback) {
    const formData = new FormData();
    for(const key in address) {
      formData.append(key, address[key]);
    }

    formData.append('caps', caps);
    formData.append('straw_hats', strawHats);
    formData.append('brimmed_hats', brimmedHats);

    for(const warehouse of warehouses) {
      formData.append('warehouses[]', warehouse);
    }

    this.postRequest('get_shipping_rates', formData, callback);
  }

}
